import { AutocompleteRenderGroupParams } from '@mui/material/Autocomplete';
import React, { useCallback, useMemo, useState } from 'react';

import { LocationSuggestionGroup } from '@abg/typings/biz/location.interface';

import {
  DEFAULT_GROUP_PREVIEW_LIMIT,
  defaultGroupLimits,
  groupPreviewLimitsMapping,
} from '../molecules/suggestion-group/suggestion-group.constants';
import {
  getGroupInfo,
  getSuggestionTypeCodeToGroupInfoMap,
} from '../molecules/suggestion-group/suggestion-group.utils';
import { SuggestionGroup } from '../molecules/suggestion-group/SuggestionGroup';

type Properties = {
  groupLimitsFromAem: Record<string, number>;
  requestedInputValue: string;
  labels: Record<string, string>;
};

export const useSuggestionGroups = ({
  groupLimitsFromAem,
  requestedInputValue,
  labels,
}: Properties) => {
  const groupLimits = useMemo(
    () => ({
      ...defaultGroupLimits,
      ...groupLimitsFromAem,
    }),
    [groupLimitsFromAem],
  );

  const { groupBackButtonLabel, ...otherLabels } = labels;

  // Get the map for suggestion type code to corresponding group titles
  const suggestionTypeCodeToGroupInfoMap = useMemo(() => {
    return getSuggestionTypeCodeToGroupInfoMap(otherLabels);
  }, [otherLabels]);

  const [selectedGroup, setSelectedGroup] =
    useState<LocationSuggestionGroup | null>(null);

  const handleGroupSelect = (group: LocationSuggestionGroup) => {
    setSelectedGroup((prevGroup) => (prevGroup === group ? null : group));
  };

  const handleGroupClear = (): void => {
    setSelectedGroup(null);
  };

  const renderGroup = useCallback(
    ({ group, children }: AutocompleteRenderGroupParams) => {
      const suggestionTypeCode = group as Exclude<
        LocationSuggestionGroup,
        'RAILWAY_BUS_STATION'
      >;

      if (!group) {
        return;
      }

      const isGroupSelected = selectedGroup === group;

      // Get the appropriate group title based on suggestion type code
      const {
        title: groupTitle = '',
        showAll = '',
        showingAll = '',
      } = getGroupInfo(suggestionTypeCode, suggestionTypeCodeToGroupInfoMap) ||
      {};

      const groupPreviewLimitKey =
        (groupPreviewLimitsMapping[
          suggestionTypeCode
        ] as keyof typeof groupLimits) || '';

      const groupPreviewLimit =
        groupPreviewLimitKey in groupLimits
          ? (groupLimits[groupPreviewLimitKey] ?? DEFAULT_GROUP_PREVIEW_LIMIT)
          : DEFAULT_GROUP_PREVIEW_LIMIT;

      return (
        <React.Fragment key={group}>
          <SuggestionGroup
            group={suggestionTypeCode}
            groupPreviewLimit={groupPreviewLimit}
            groupItems={children}
            isGroupSelected={isGroupSelected}
            requestedInputValue={requestedInputValue}
            labels={{
              groupTitle,
              showAll,
              showingAll,
              back: groupBackButtonLabel ?? '',
            }}
            onBack={() => handleGroupClear()}
            onSelect={handleGroupSelect}
          />
        </React.Fragment>
      );
    },
    [
      groupLimits,
      requestedInputValue,
      groupBackButtonLabel,
      selectedGroup,
      suggestionTypeCodeToGroupInfoMap,
    ],
  );

  return {
    selectedGroup,
    handleGroupClear,
    renderGroup,
  };
};
